import { useMemo } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../stores/db'
import usePrefStore from '../stores/prefs'
import useCollections from './useCollections'
import useRetailCollections from './useRetailCollections'
import useRetailCatalogScope from './useRetailCatalogScope'
import { computeCanSeeJewelry } from './useCanSeeJewelry'
import glob_const from '../libs/glob_const'

export interface CatalogScopeFlags {
   hasRetail: boolean    // au moins une famille retail visible pour le customer courant
   hasJewelry: boolean   // catalogue bijoux visible (rôle) ET au moins une gamme avec modèles
   isRetailOnly: boolean // hasRetail && !hasJewelry — le catalogue n'affiche QUE du retail
   hasBoth: boolean      // hasRetail && hasJewelry — retail ET bijoux affichés en même temps
}

/**
 * Flags de périmètre du catalogue (retail / bijoux), répliques fidèles de la
 * logique de Catalog.tsx (hasJewelry / hasRetail). Sert à choisir partout le
 * picto "produit" (boîte vs diamant) et à conditionner le filtre RET.
 *
 * - hasJewelry : catalogue bijoux visible pour le rôle ET au moins une gamme
 *   ayant des modèles (mêmes règles que Catalog.tsx).
 * - hasRetail  : retail activé ET au moins une famille retail visible pour le
 *   customer courant (via useRetailCatalogScope, comme Catalog.tsx).
 */
export function useCatalogScopeFlags(): CatalogScopeFlags {
   const { retailRole, serverRetailRole, isRetailManager, prefsLoaded, curCustomer } = usePrefStore()
   const { getGammesAsSortedArray, getAllModelsAsArray } = useCollections()
   const { getAllRetailModelsAsArray, isRetailModelVisibleForCustomer } = useRetailCollections()
   const { customerType: effectiveCustomerType, customerForGrid } = useRetailCatalogScope()

   // exclu_boutique du customer courant — lu directement en Dexie pour ne pas
   // tirer un second useSales (useRetailCatalogScope en utilise déjà un).
   const curCustomerObj = useLiveQuery(
      async () => (curCustomer ? (await db.customers.get(curCustomer)) ?? null : null),
      [curCustomer],
   )
   const hasExcluBoutique = curCustomerObj?.exclu_boutique != null

   // Catalogue bijoux visible (rôle) — source unique partagée (cf. useCanSeeJewelry).
   const showJewelryCatalog = useMemo(
      () => computeCanSeeJewelry(retailRole, serverRetailRole, isRetailManager, prefsLoaded),
      [retailRole, serverRetailRole, isRetailManager, prefsLoaded],
   )

   const hasJewelry = useMemo(() => {
      if (!showJewelryCatalog) return false
      const gammesWithModels = new Set<string>()
      for (const model of getAllModelsAsArray()) {
         const g = (model as unknown as Record<string, unknown>).gamme as string | undefined
         if (g) gammesWithModels.add(g)
      }
      return getGammesAsSortedArray().some(g => gammesWithModels.has(g.id))
   }, [showJewelryCatalog, getAllModelsAsArray, getGammesAsSortedArray])

   const hasRetail = useMemo(() => {
      if (!glob_const.retail_enabled) return false
      for (const model of getAllRetailModelsAsArray()) {
         if (isRetailModelVisibleForCustomer(model, effectiveCustomerType, hasExcluBoutique, customerForGrid)) return true
      }
      return false
   }, [getAllRetailModelsAsArray, isRetailModelVisibleForCustomer, effectiveCustomerType, hasExcluBoutique, customerForGrid])

   return {
      hasRetail,
      hasJewelry,
      isRetailOnly: hasRetail && !hasJewelry,
      hasBoth: hasRetail && hasJewelry,
   }
}

/**
 * Raccourci booléen : le catalogue n'affiche QUE du retail.
 * (Wrapper sur useCatalogScopeFlags pour les appelants qui n'ont besoin que de ça.)
 */
export default function useIsRetailOnly(): boolean {
   return useCatalogScopeFlags().isRetailOnly
}
